/**
 * Error monitoring utility to help identify the source of async errors
 */

interface ErrorInfo {
  message: string;
  stack?: string;
  timestamp: number;
  url: string;
  userAgent: string;
  isIncognito?: boolean;
}

class ErrorMonitor {
  private static instance: ErrorMonitor;
  private errors: ErrorInfo[] = [];

  static getInstance(): ErrorMonitor {
    if (!ErrorMonitor.instance) {
      ErrorMonitor.instance = new ErrorMonitor();
    }
    return ErrorMonitor.instance;
  }

  init() {
    // Monitor unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        message: event.reason?.message || 'Unhandled Promise Rejection',
        stack: event.reason?.stack,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        isIncognito: this.detectIncognito(),
      });
    });

    // Monitor global errors
    window.addEventListener('error', (event) => {
      this.logError({
        message: event.message,
        stack: event.error?.stack,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        isIncognito: this.detectIncognito(),
      });
    });

    console.log('[ErrorMonitor] Initialized - monitoring async errors');
  }

  private detectIncognito(): boolean {
    // Simple incognito detection
    try {
      const testKey = '__incognito_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return false;
    } catch {
      return true;
    }
  }

  private logError(error: ErrorInfo) {
    this.errors.push(error);
    
    // Keep only last 50 errors
    if (this.errors.length > 50) {
      this.errors = this.errors.slice(-50);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`[ErrorMonitor] ${error.message}`);
      console.log('Timestamp:', new Date(error.timestamp).toISOString());
      console.log('URL:', error.url);
      console.log('Incognito:', error.isIncognito);
      console.log('Stack:', error.stack);
      console.groupEnd();
    }
  }

  getErrors(): ErrorInfo[] {
    return [...this.errors];
  }

  getAsyncErrors(): ErrorInfo[] {
    return this.errors.filter(error => 
      error.message.includes('asynchronous response') ||
      error.message.includes('message channel')
    );
  }

  clearErrors() {
    this.errors = [];
  }

  // Export errors for debugging
  exportErrors(): string {
    return JSON.stringify(this.errors, null, 2);
  }
}

export const errorMonitor = ErrorMonitor.getInstance();

// Auto-initialize in development
if (process.env.NODE_ENV === 'development') {
  errorMonitor.init();
} 